import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/useAuth';
import AppShell from '@/components/Layout/AppShell';

// Skeleton fallback for lazy-loaded pages
function PageSkeleton() {
  return (
    <div className="animate-pulse space-y-4 p-6">
      <div className="h-8 w-48 rounded bg-slate-200" />
      <div className="h-4 w-96 rounded bg-slate-200" />
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="h-32 rounded-lg bg-slate-200" />
        <div className="h-32 rounded-lg bg-slate-200" />
        <div className="h-32 rounded-lg bg-slate-200" />
      </div>
      <div className="mt-4 h-64 rounded-lg bg-slate-200" />
    </div>
  );
}

// Lazy-loaded pages
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const AssetList = React.lazy(() => import('@/pages/AssetList'));
const AssetDetail = React.lazy(() => import('@/pages/AssetDetail'));
const DocumentList = React.lazy(() => import('@/pages/DocumentList'));
const DocumentDetail = React.lazy(() => import('@/pages/DocumentDetail'));
const ComplianceOverview = React.lazy(() => import('@/pages/ComplianceOverview'));
const ComplianceDetail = React.lazy(() => import('@/pages/ComplianceDetail'));
const AMCContractList = React.lazy(() => import('@/pages/AMCContractList'));
const AMCContractDetail = React.lazy(() => import('@/pages/AMCContractDetail'));
const TaskList = React.lazy(() => import('@/pages/TaskList'));
const NotificationCenter = React.lazy(() => import('@/pages/NotificationCenter'));
const AuditLogViewer = React.lazy(() => import('@/pages/AuditLogViewer'));
const Settings = React.lazy(() => import('@/pages/Settings'));
const SettingsCategories = React.lazy(() => import('@/pages/SettingsCategories'));
const SettingsLocations = React.lazy(() => import('@/pages/SettingsLocations'));
const SettingsWorkflows = React.lazy(() => import('@/pages/SettingsWorkflows'));
const SettingsTemplates = React.lazy(() => import('@/pages/SettingsTemplates'));
const PendingApprovals = React.lazy(() => import('@/pages/PendingApprovals'));
const ApprovalDetail = React.lazy(() => import('@/pages/ApprovalDetail'));
const ApprovalChainSettings = React.lazy(() => import('@/pages/ApprovalChainSettings'));
const AuthCallback = React.lazy(() => import('@/pages/AuthCallback'));
const VendorList = React.lazy(() => import('@/pages/VendorList'));
const VendorDetail = React.lazy(() => import('@/pages/VendorDetail'));
const VendorForm = React.lazy(() => import('@/pages/VendorForm'));
const WhatsAppSettings = React.lazy(() => import('@/pages/WhatsAppSettings'));
const VendorPortalDashboard = React.lazy(() => import('@/pages/VendorPortalDashboard'));
const VendorPortalJobs = React.lazy(() => import('@/pages/VendorPortalJobs'));
const VendorPortalJobDetail = React.lazy(() => import('@/pages/VendorPortalJobDetail'));
const WhatsAppTemplates = React.lazy(() => import('@/pages/WhatsAppTemplates'));
const WhatsAppMessageLog = React.lazy(() => import('@/pages/WhatsAppMessageLog'));
const CalendarPage = React.lazy(() => import('@/pages/CalendarPage'));
const TaskRoutingSettings = React.lazy(() => import('@/pages/TaskRoutingSettings'));
const VendorPortalProfile = React.lazy(() => import('@/pages/VendorPortalProfile'));
const UserManagement = React.lazy(() => import('@/pages/UserManagement'));
const SOPConfig = React.lazy(() => import('@/pages/SOPConfig'));
const SOPList = React.lazy(() => import('@/pages/SOPList'));
const SOPDetail = React.lazy(() => import('@/pages/SOPDetail'));
const SOPEditor = React.lazy(() => import('@/pages/SOPEditor'));
const SOPGenerator = React.lazy(() => import('@/pages/SOPGenerator'));
const TrainingDashboard = React.lazy(() => import('@/pages/TrainingDashboard'));
const TrainingAssignment = React.lazy(() => import('@/pages/TrainingAssignment'));
const CAPAList = React.lazy(() => import('@/pages/CAPAList'));
const CAPADetail = React.lazy(() => import('@/pages/CAPADetail'));
const ChangeControlList = React.lazy(() => import('@/pages/ChangeControlList'));
const ChangeRequestDetail = React.lazy(() => import('@/pages/ChangeRequestDetail'));
const RiskRegister = React.lazy(() => import('@/pages/RiskRegister'));
const RiskAssessmentDetail = React.lazy(() => import('@/pages/RiskAssessmentDetail'));
const QMSDashboard = React.lazy(() => import('@/pages/QMSDashboard'));

// Forbidden page
export function ForbiddenPage() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
      <div className="rounded-full bg-red-100 p-4">
        <svg className="h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      </div>
      <h1 className="mt-4 text-2xl font-bold text-slate-900">403 — Access Denied</h1>
      <p className="mt-2 text-slate-600">You don't have permission to access this page.</p>
      <a href="/" className="mt-6 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors">
        Return to Dashboard
      </a>
    </div>
  );
}

// Role guard component
interface RoleGuardProps {
  allowedRoles: string[];
  children: React.ReactNode;
}

export function RoleGuard({ allowedRoles, children }: RoleGuardProps) {
  const { role } = usePermissions();

  // In local dev (no auth), role may be undefined — allow access
  if (!role) {
    return <>{children}</>;
  }

  if (!allowedRoles.includes(role)) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
}

// Vendor Portal guard — only vendor_user can access portal routes;
// non-vendor users are redirected to home.
function VendorPortalGuard({ children }: { children: React.ReactNode }) {
  const { role } = usePermissions();

  // Only block if role is explicitly NOT vendor_user (and role exists)
  if (role && role !== 'vendor_user') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Internal user guard — vendor_user role is redirected to /portal
function InternalUserGuard({ children }: { children: React.ReactNode }) {
  const { role } = usePermissions();

  if (role === 'vendor_user') {
    return <Navigate to="/portal" replace />;
  }

  return <>{children}</>;
}

// Main app routes
export default function AppRoutes() {
  return (
    <Routes>
      {/* Auth callback — outside the shell */}
      <Route
        path="/auth/callback"
        element={
          <Suspense fallback={<PageSkeleton />}>
            <AuthCallback />
          </Suspense>
        }
      />

      {/* Forbidden page — inside shell */}
      <Route
        path="/403"
        element={
          <AppShell>
            <ForbiddenPage />
          </AppShell>
        }
      />

      {/* Authenticated pages wrapped in AppShell */}
      <Route
        path="/"
        element={
          <AppShell>
            <InternalUserGuard>
              <Suspense fallback={<PageSkeleton />}>
                <Dashboard />
              </Suspense>
            </InternalUserGuard>
          </AppShell>
        }
      />
      <Route
        path="/assets"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <AssetList />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/assets/:id"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <AssetDetail />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/documents"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <DocumentList />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/documents/:id"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <DocumentDetail />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/compliance"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <ComplianceOverview />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/compliance/:id"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <ComplianceDetail />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/amc"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <AMCContractList />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/amc/:id"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <AMCContractDetail />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/tasks"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <TaskList />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/approvals"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <PendingApprovals />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/approvals/:id"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <ApprovalDetail />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/notifications"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <NotificationCenter />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin', 'auditor']}>
              <Suspense fallback={<PageSkeleton />}>
                <AuditLogViewer />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />
      <Route
        path="/settings"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <Settings />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/settings/categories"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <SettingsCategories />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />
      <Route
        path="/settings/locations"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <SettingsLocations />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />
      <Route
        path="/settings/workflows"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <SettingsWorkflows />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />
      <Route
        path="/settings/templates"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <SettingsTemplates />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />
      <Route
        path="/settings/approval-chains"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <ApprovalChainSettings />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />

      <Route
        path="/settings/whatsapp"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <WhatsAppSettings />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/settings/sop-config"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <SOPConfig />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />
      <Route
        path="/settings/users"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <UserManagement />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/settings/whatsapp/messages"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <WhatsAppMessageLog />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />

      <Route
        path="/vendors"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin', 'maintenance_manager']}>
              <Suspense fallback={<PageSkeleton />}>
                <VendorList />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />
      <Route
        path="/vendors/new"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin', 'maintenance_manager']}>
              <Suspense fallback={<PageSkeleton />}>
                <VendorForm />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />
      <Route
        path="/vendors/:id/edit"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin', 'maintenance_manager']}>
              <Suspense fallback={<PageSkeleton />}>
                <VendorForm />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />
      <Route
        path="/vendors/:id"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin', 'maintenance_manager']}>
              <Suspense fallback={<PageSkeleton />}>
                <VendorDetail />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />

      <Route
        path="/settings/whatsapp/templates"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin']}>
              <Suspense fallback={<PageSkeleton />}>
                <WhatsAppTemplates />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />

      <Route
        path="/settings/task-routing"
        element={
          <AppShell>
            <RoleGuard allowedRoles={['organization_admin', 'maintenance_manager']}>
              <Suspense fallback={<PageSkeleton />}>
                <TaskRoutingSettings />
              </Suspense>
            </RoleGuard>
          </AppShell>
        }
      />

      <Route
        path="/calendar"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <CalendarPage />
            </Suspense>
          </AppShell>
        }
      />

      {/* SOP/QMS Module Routes */}
      <Route
        path="/sops"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <SOPList />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/sops/generate"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <SOPGenerator />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/sops/:id/edit"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <SOPEditor />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/sops/:id"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <SOPDetail />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/training"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <TrainingDashboard />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/training/:id"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <TrainingAssignment />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/capa"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <CAPAList />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/capa/:id"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <CAPADetail />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/change-control"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <ChangeControlList />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/change-control/:id"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <ChangeRequestDetail />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/risk-management"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <RiskRegister />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/risk-management/:id"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <RiskAssessmentDetail />
            </Suspense>
          </AppShell>
        }
      />
      <Route
        path="/qms-dashboard"
        element={
          <AppShell>
            <Suspense fallback={<PageSkeleton />}>
              <QMSDashboard />
            </Suspense>
          </AppShell>
        }
      />

      {/* Vendor Portal — self-contained layout, vendor_user role only */}
      <Route
        path="/portal"
        element={
          <VendorPortalGuard>
            <Suspense fallback={<PageSkeleton />}>
              <VendorPortalDashboard />
            </Suspense>
          </VendorPortalGuard>
        }
      />
      <Route
        path="/portal/jobs"
        element={
          <VendorPortalGuard>
            <Suspense fallback={<PageSkeleton />}>
              <VendorPortalJobs />
            </Suspense>
          </VendorPortalGuard>
        }
      />
      <Route
        path="/portal/jobs/:id"
        element={
          <VendorPortalGuard>
            <Suspense fallback={<PageSkeleton />}>
              <VendorPortalJobDetail />
            </Suspense>
          </VendorPortalGuard>
        }
      />
      <Route
        path="/portal/profile"
        element={
          <VendorPortalGuard>
            <Suspense fallback={<PageSkeleton />}>
              <VendorPortalProfile />
            </Suspense>
          </VendorPortalGuard>
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
